import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import apiClient from '../utils/apiClient';
import '../styles/authModal.css';
import logger from '../utils/logger';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../utils/firebaseConfig';
// Import eye icons for password visibility toggle
import eyeShowIcon from '../images/icons/eye-show.svg';
import eyeHideIcon from '../images/icons/eye-hide.svg';

const FirebaseAuthModal = ({ onClose, onAuthenticated, initialMode, initialEmail, openSignup }) => {
    const [isLogin, setIsLogin] = useState(initialMode === 'signup' ? false : true);
    const [email, setEmail] = useState(initialEmail !== undefined ? initialEmail : '');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    // Add state for password visibility
    const [showPassword, setShowPassword] = useState(false);
    // Create a ref for the error message div
    const errorMessageRef = useRef(null);
    
    const navigate = useNavigate();
    const location = useLocation();
    const { signIn, signUp, signInWithGoogle, user: contextUser } = useFirebase();
    
    // Update login state if initialMode changes
    useEffect(() => {
        if (initialMode === 'signup') {
            setIsLogin(false);
        }
    }, [initialMode]);
    
    // Update email if initialEmail changes
    useEffect(() => {
        if (initialEmail) {
            setEmail(initialEmail);
        }
    }, [initialEmail]);

    // Add effect to scroll to error message when it appears
    useEffect(() => {
        if (error && errorMessageRef.current) {
            console.log('Error message updated:', error);
            errorMessageRef.current.scrollIntoView({ behavior: 'smooth' });
            
            // Apply a flash animation by toggling a class
            errorMessageRef.current.classList.add('flash-error');
            // Make sure the error is visible
            errorMessageRef.current.style.display = 'block';
            
            setTimeout(() => {
                if (errorMessageRef.current) {
                    errorMessageRef.current.classList.remove('flash-error');
                }
            }, 1000);
        }
    }, [error]);
    
    // Add this CSS style to the head of the component
    useEffect(() => {
        // Add the flash-error animation style and password field styling
        const style = document.createElement('style');
        style.textContent = `
            .flash-error {
                animation: flashError 0.5s ease;
            }
            
            @keyframes flashError {
                0% { opacity: 0; transform: translateY(-10px); }
                100% { opacity: 1; transform: translateY(0); }
            }
            
            .fullscreen-auth-error-direct {
                padding: 15px !important;
                margin: 0 0 20px 0 !important;
                background-color: rgba(255, 59, 48, 0.3) !important;
                border: 2px solid #ff3b30 !important;
                border-radius: 8px !important;
                color: white !important;
                font-weight: bold !important;
                text-align: center !important;
            }
            
            .password-input-container {
                position: relative;
                width: 100%;
                margin-bottom: 20px;
            }
            
            .password-input-container .fullscreen-auth-input {
                padding-right: 50px;
                margin-bottom: 0;
            }
            
            .password-toggle-icon {
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                width: 24px;
                height: 24px;
                cursor: pointer;
                user-select: none;
                background-repeat: no-repeat;
                background-position: center;
                background-size: contain;
                opacity: 0.7;
                transition: opacity 0.2s;
            }
            
            .password-toggle-icon:hover {
                opacity: 1;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    
    // Add state persistence for errors
    useEffect(() => {
        // Check for stored errors on component mount
        const storedError = sessionStorage.getItem('auth_error');
        if (storedError) {
            setError(storedError);
            // Clear after retrieving to avoid showing old errors on future mounts
            sessionStorage.removeItem('auth_error');
        }
    }, []);

    // When error changes, store it in session storage
    useEffect(() => {
        if (error) {
            sessionStorage.setItem('auth_error', error);
        }
    }, [error]);
    
    // Function to switch between login and signup modes
    const toggleAuthMode = (mode) => {
        if (mode === 'signup') {
            setIsLogin(false);
            // Update URL parameter
            navigate('?auth=signup', { replace: true });
        } else {
            setIsLogin(true);
            // Update URL parameter
            navigate('?auth=login', { replace: true });
        }
    };
    
    const handleSubmit = async (e) => {
        console.log('Login form submitted - starting authentication process');
        e.preventDefault(); // Prevent form submission and page reload
        setError('');
        console.log('Error state cleared, setting loading state to true');
        setLoading(true);
        
        try {
            const idToken = isLogin ? await signIn(email, password) : await signUp(email, password);

            if (!isLogin) {
                 // After successful signup, create the user in MongoDB
                try {
                    // Get username from localStorage if it was set during the signup process
                    const username = localStorage.getItem('username');
                    
                    // Create user in MongoDB using apiClient
                    // apiClient.put() returns parsed JSON on success, or throws on HTTP error
                    const userData = await apiClient.put('/api/profile', {
                        username: username || email.split('@')[0]
                    });
                    
                    // Update username in localStorage if returned from server
                    if (userData && userData.username) {
                        localStorage.setItem('username', userData.username);
                        console.log('User profile created in MongoDB successfully');
                    } else {
                        console.error('Failed to create user profile in MongoDB');
                    }
                } catch (profileError) {
                    console.error('Error creating user profile in MongoDB:', profileError);
                    // Don't throw error here to allow the auth process to complete
                }
            }
            
            if (idToken) { // Ensure idToken is valid
                onAuthenticated({
                    accessToken: idToken,
                    idToken: idToken, // Or just idToken if accessToken is not specifically needed by onAuthenticated
                    user: { // Construct user object from contextUser
                        email: contextUser?.email, // Use optional chaining for safety
                        name: contextUser?.displayName || contextUser?.email,
                        sub: contextUser?.uid
                    }
                });
                onClose(); // Assuming onClose is called after successful authentication
            }
        } catch (err) {
            console.error('Authentication error:', err);
            
            // Get the error message
            let errorMessage;
            if (err.message && !err.code) {
                errorMessage = err.message;
            } else {
                errorMessage = getAuthErrorMessage(err.code);
            }
            
            // Set the error state
            setError(errorMessage);
            console.log('Setting error message:', errorMessage);
            // Store in session storage to persist across renders
            sessionStorage.setItem('auth_error', errorMessage);
            
            // Make sure the error message is visible by directly updating DOM if necessary
            if (errorMessageRef.current) {
                errorMessageRef.current.style.display = 'block';
                errorMessageRef.current.innerHTML = `<strong>Error:</strong> ${errorMessage}`;
                errorMessageRef.current.classList.add('flash-error');
                setTimeout(() => {
                    if (errorMessageRef.current) {
                        errorMessageRef.current.classList.remove('flash-error');
                    }
                }, 1000);
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleGoogleSignIn = async () => {
        setError('');
        setLoading(true);
        
        try {
            // Get token directly from Google sign-in to avoid race with onAuthStateChanged
            const idToken = await signInWithGoogle();
            
            // LOGIN MODE: Check if user exists in MongoDB
            try {
                logger.debug('FirebaseAuthModal - Checking if Google user exists in MongoDB (LOGIN mode)');
                const response = await apiClient.fetchWithAuth('/api/profile', { method: 'GET' });
                if (response.status === 200) {
                    const userData = await response.json();
                    if (!userData.username || userData.needsSetup) {
                        logger.info('FirebaseAuthModal - Google user missing username, redirecting to account setup');
                        localStorage.setItem('needsAccountSetup', 'true');
                        onClose();
                        navigate('/account-setup', { replace: true });
                        return;
                    }
                    if (userData.username) localStorage.setItem('username', userData.username);
                } else if (response.status === 401 || response.status === 404) {
                    logger.info('FirebaseAuthModal - New Google user (LOGIN mode), redirecting to account setup');
                    localStorage.setItem('needsAccountSetup', 'true');
                    onClose();
                    navigate('/account-setup', { replace: true });
                    return;
                } else {
                    // Non-auth errors: show error and stop, do NOT redirect to setup
                    const text = await response.text().catch(() => '');
                    logger.error('FirebaseAuthModal - Unexpected error loading profile:', text);
                    setError('Could not load your profile. Please try again.');
                    setLoading(false);
                    return;
                }
            } catch (profileError) {
                logger.error('FirebaseAuthModal - Error checking profile (network/other):', profileError);
                setError('Could not reach server. Please try again.');
                setLoading(false);
                return;
            }
            
            if (idToken) {
                onAuthenticated({
                    accessToken: idToken,
                    idToken,
                    user: {
                        email: contextUser?.email,
                        name: contextUser?.displayName || contextUser?.email,
                        sub: contextUser?.uid
                    }
                });
                onClose();
            }
        } catch (err) {
            console.error('Google sign-in error:', err);
            
            // Handle error
            setError(getAuthErrorMessage(err.code) || 'An error occurred during Google sign-in');
        } finally {
            setLoading(false);
        }
    };
    
    // Helper function to translate Firebase error codes into user-friendly messages
    const getAuthErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/invalid-email':
                return 'Invalid email address format.';
            case 'auth/user-disabled':
                return 'This account has been disabled.';
            case 'auth/user-not-found':
                return 'Wrong email or password. Please try again.';
            case 'auth/wrong-password':
                return 'Wrong email or password. Please try again.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists.';
            case 'auth/weak-password':
                return 'Password is too weak. Use at least 6 characters.';
            case 'auth/popup-closed-by-user':
                return 'Sign-in popup was closed before completing the sign in.';
            case 'auth/invalid-credential':
                return 'Wrong email or password. Please try again.';
            case 'auth/invalid-login-credentials':
                return 'Wrong email or password. Please try again.';
            default:
                return 'An error occurred during authentication. Please try again.';
        }
    };
    
    // Handle password reset
    const handleForgotPassword = async (e) => {
        e.preventDefault();
        
        if (!email) {
            setError('Please enter your email address to reset your password.');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            await sendPasswordResetEmail(auth, email);
            setResetSent(true);
            logger.info('FirebaseAuthModal - Password reset email sent');
        } catch (err) {
            logger.error('FirebaseAuthModal - Error sending password reset:', err);
            
            if (err.code === 'auth/user-not-found') {
                setError('No account found with this email address.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else {
                setError('Error sending password reset email. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Add password visibility toggle handlers
    const handlePasswordMouseDown = () => {
        setShowPassword(true);
    };

    const handlePasswordMouseUp = () => {
        setShowPassword(false);
    };

    // Handle mouse leave to ensure password is hidden if mouse leaves while held down
    const handlePasswordMouseLeave = () => {
        setShowPassword(false);
    };

    // Handle touch events for mobile devices
    const handlePasswordTouchStart = (e) => {
        e.preventDefault(); // Prevent default behavior
        setShowPassword(true);
    };

    const handlePasswordTouchEnd = (e) => {
        e.preventDefault(); // Prevent default behavior
        setShowPassword(false);
    };

    const handlePasswordTouchCancel = (e) => {
        e.preventDefault(); // Prevent default behavior
        setShowPassword(false);
    };
    
    return (
        <div className="fullscreen-auth-overlay" onClick={onClose}>
            <div className="fullscreen-auth-content" onClick={(e) => e.stopPropagation()}>
                <button className="fullscreen-auth-close" onClick={onClose}>×</button>
                
                <div className="fullscreen-auth-form">
                    <h2>{isLogin ? 'Log In' : 'Sign Up'}</h2>
                    
                    {/* Password reset success message */}
                    {resetSent && (
                        <div 
                            className="fullscreen-auth-success"
                            style={{ 
                                padding: '15px',
                                margin: '0 0 20px 0',
                                backgroundColor: 'rgba(76, 217, 100, 0.2)',
                                border: '2px solid #4cd964',
                                borderRadius: '8px',
                                color: 'white',
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            Password reset email sent! Please check your inbox.
                        </div>
                    )}
                    
                    {/* Direct error message display - enhanced visibility and positioning */}
                    {error && (
                        <div 
                            ref={errorMessageRef}
                            className="fullscreen-auth-error-direct"
                            style={{ 
                                display: 'block',
                                padding: '15px',
                                margin: '0 0 20px 0',
                                backgroundColor: 'rgba(255, 59, 48, 0.4)',
                                border: '2px solid #ff3b30',
                                borderRadius: '8px',
                                color: 'white',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                maxWidth: '100%',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10
                            }}
                        >
                            <strong>Error:</strong> {error}
                        </div>
                    )}
                    
                    <form style={{width: '100%'}} onSubmit={handleSubmit}>
                        <input
                            className="fullscreen-auth-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            required
                        />
                        
                        <div className="password-input-container">
                            <input
                                className="fullscreen-auth-input"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                required
                            />
                            <div 
                                className="password-toggle-icon"
                                onMouseDown={handlePasswordMouseDown}
                                onMouseUp={handlePasswordMouseUp}
                                onMouseLeave={handlePasswordMouseLeave}
                                onTouchStart={handlePasswordTouchStart}
                                onTouchEnd={handlePasswordTouchEnd}
                                onTouchCancel={handlePasswordTouchCancel}
                                style={{
                                    backgroundImage: `url(${showPassword ? eyeShowIcon : eyeHideIcon})`,
                                    cursor: 'pointer'
                                }}
                                role="button"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                tabIndex="0"
                            />
                        </div>
                        
                        {/* Forgot Password link */}
                        {isLogin && (
                            <div className="fullscreen-auth-forgot-password">
                                <button 
                                    type="button"
                                    className="fullscreen-auth-link"
                                    onClick={handleForgotPassword}
                                    disabled={loading}
                                >
                                    Forgot Password?
                                </button>
                            </div>
                        )}
                        
                        <button 
                            type="submit" 
                            className="fullscreen-auth-button"
                            disabled={loading}
                        >
                            {loading ? 'Please wait...' : (isLogin ? 'Log In' : 'Sign Up')}
                        </button>
                    </form>
                    
                    <div className="fullscreen-auth-separator">
                        <span>OR</span>
                    </div>
                    
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        className="fullscreen-auth-google"
                        disabled={loading}
                        aria-label={isLogin ? "Sign in with Google" : "Sign up with Google"}
                    >
                        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12
                            s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24
                            s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039
                            l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36
                            c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571
                            c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                        </svg>
                        {loading ? 'Please wait...' : isLogin ? 'Sign in with Google' : 'Sign up with Google'}
                    </button>
                    
                    {/* Add helper text to explain the button behavior */}
                    <p className="fullscreen-auth-helper-text">
                        {isLogin ? 
                            "New users will be redirected to account setup" : 
                            "Your account will be created with your selections"}
                    </p>
                    
                    <div className="fullscreen-auth-toggle">
                        {isLogin ? (
                            <p>
                                Don't have an account?{' '}
                                <button 
                                    className="fullscreen-auth-link" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (openSignup) {
                                            openSignup();
                                        } else {
                                            toggleAuthMode('signup');
                                        }
                                    }}
                                >
                                    Sign Up
                                </button>
                            </p>
                        ) : (
                            <p>
                                Already have an account?{' '}
                                <button 
                                    className="fullscreen-auth-link" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleAuthMode('login');
                                    }}
                                >
                                    Log In
                                </button>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

FirebaseAuthModal.defaultProps = {
    initialMode: 'login',
    initialEmail: '',
    openSignup: null,
};

export default FirebaseAuthModal;